import { DURATION } from "@hey-lady/shared/helpers/const";
import {
  translateEnglishSkill,
  translateEnum,
  translateInterest,
} from "@hey-lady/shared/helpers/utils";
import { isAdminOrTeam, isStaff } from "@hey-lady/shared/hooks/event";
import { useFormatMessages } from "@hey-lady/shared/hooks/general";
import { useAuth } from "@hey-lady/shared/hooks/user";
import {
  EnglishSkill,
  EventEnglishLevelFilter,
  EventHostedByFilter,
  EventPermissions,
  EventTimeFilter,
  EventTopicFilter,
  EventTypeFilter,
  Interest,
  OccupationModel,
  UserLocation,
  useGetGroupedOccupationsQuery,
  useGetOccupationsQuery,
} from "@hey-lady/shared/types/graphql";
import LogoBranding from "@hey-lady/ui/atoms/LogoBranding";
import { format, parse } from "date-fns";
import { isNotEmpty, isNotNil, not, values } from "ramda";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";
import {
  AppliedFilterType,
  AVAILABILITY_FILTER,
  DEFAULT_EVENTS_FILTERS,
  ENGLISH_SKILLS_FILTER,
  EVENT_DATE_FILTER,
  EVENT_DURATION_FILTER,
  EVENT_FULL_EVENTS_FILTER,
  EVENT_TOPIC_ID_FILTER,
  EVENTS_TO_UNLOCK,
  EventFilters,
  Filters,
  INTERESTS_FILTER,
  LOCATION_FILTER,
  MemberType,
  OCCUPATION_FILTER,
  PeopleEnglishLevelOptions,
  UrlFilterParams,
} from "./consts";
import { getEventTimeFiltersFromParam } from "./EventsFilters/eventTime";
import { isPostReleaseMember, translateEventDurationFilter } from "./helpers";

export const useAppliedMembersFilters = (filters: Filters["people"]) => {
  const intl = useIntl();

  const [members, peopleIFollow, littleSisters, bigSisters, intermediate, advanced, proficient] =
    useFormatMessages([
      { id: "COMMON_FILTERS_MEMBERS" },
      { id: "PEOPLE_PEOPLE_I_FOLLOW" },
      { id: "PEOPLE_LITTLE_SISTERS" },
      { id: "PEOPLE_BIG_SISTERS" },
      { id: "PEOPLE_SEARCH_INTERMEDIATE" },
      { id: "PEOPLE_SEARCH_ADVANCED" },
      { id: "PEOPLE_SEARCH_PROFICIENT" },
    ]);

  const filterLabels = new Map<MemberType | PeopleEnglishLevelOptions, string>([
    [MemberType.LittleSisters, littleSisters as string],
    [MemberType.Members, members as string],
    [MemberType.BigSisters, bigSisters as string],
    [MemberType.Follow, peopleIFollow as string],
    [PeopleEnglishLevelOptions.Intermediate, intermediate as string],
    [PeopleEnglishLevelOptions.Advanced, advanced as string],
    [PeopleEnglishLevelOptions.Proficient, proficient as string],
  ]);

  const newFilters: AppliedFilterType[] = [];

  for (const [key, value] of Object.entries(filters)) {
    if (key === AVAILABILITY_FILTER) {
      continue;
    }
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (key === LOCATION_FILTER) {
          const location = v as UserLocation;
          if (location.city) {
            newFilters.push({ category: key, value: location.city, label: location.city });
          } else if (location.country) {
            newFilters.push({
              category: key,
              value: location.country,
              label: location.country,
            });
          }
        } else if (key === INTERESTS_FILTER) {
          const interest = v;
          newFilters.push({
            category: key,
            value: v as string,
            label: translateInterest(interest as Interest, intl).title,
          });
        } else if (key === ENGLISH_SKILLS_FILTER) {
          newFilters.push({
            category: key,
            value: v as string,
            label: translateEnglishSkill(v as EnglishSkill, intl).title,
          });
        } else if (key === OCCUPATION_FILTER) {
          const occupation = v as OccupationModel;
          newFilters.push({
            category: key,
            value: occupation.id,
            label: occupation.name || "",
          });
        } else {
          const labels = filterLabels.get(v as MemberType | PeopleEnglishLevelOptions);
          if (labels) {
            newFilters.push({
              category: key,
              value: v as string,
              label: labels,
            });
          }
        }
      });
    }
  }
  return newFilters;
};

type EventsFiltersOptions =
  | EventHostedByFilter
  | EventTypeFilter
  | EventEnglishLevelFilter
  | EventTopicFilter;

export const useAppliedEventsFilters = (filters: EventFilters["events"]) => {
  const intl = useIntl();
  const [fullEvents, removeTopic] = useFormatMessages([
    { id: "EVENTS_PAGE_FULL_EVENTS" },
    { id: "EVENTS_PAGE_REMOVE_TOPIC" },
  ]);

  const { me } = useAuth();
  const attendedEventsCount = me?.eventStatistics?.totalAttendedScheduledEvents || 0;

  const eventTypeOptions = useEventTypeOptions(attendedEventsCount);
  const hostedByOptions = useEventHostedByOptions(attendedEventsCount, me?.createdAt ?? undefined);
  const englishLevelOptions = useEventEnglishLevelOptions();
  const topicFilterOptions = useEventTopicFilterOptions();

  const eventFiltersOptions = [
    ...hostedByOptions,
    ...eventTypeOptions,
    ...englishLevelOptions,
    ...topicFilterOptions,
  ];
  const mappedEventFiltersOptions = new Map(
    eventFiltersOptions.map((option) => [option.id, option.name]),
  );

  const occupationsOptions = useGetGroupedOccupationsOptions();

  const newFilters: AppliedFilterType[] = [];
  const occupations = (filters.occupations ?? [])
    .map((id) => occupationsOptions.find((occupation) => occupation.id === id))
    .filter(isNotNil) as OccupationModel[];

  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value)) {
      value.forEach((val) => {
        if (key === INTERESTS_FILTER) {
          newFilters.push({
            category: key,
            value: val as string,
            label: translateInterest(val as Interest, intl).title,
          });
        } else if (key === ENGLISH_SKILLS_FILTER) {
          newFilters.push({
            category: key,
            value: val as string,
            label: translateEnglishSkill(val as EnglishSkill, intl).title,
          });
        } else if (key === OCCUPATION_FILTER) {
          const occupation = occupations.find((o) => o.id === val);
          if (occupation) {
            newFilters.push({
              category: key,
              value: occupation.id,
              label: occupation.name || "",
            });
          }
        } else if (key === EVENT_DURATION_FILTER) {
          newFilters.push({
            category: key,
            value: val.toString(),
            label: translateEventDurationFilter(val as number, intl).title,
          });
        }
      });
    } else {
      if (key === EVENT_DATE_FILTER && value) {
        newFilters.push({
          category: key,
          value: value as string,
          label: format(parse(value as string, "yyyy-MM-dd", new Date()), "EEEE, LLL do, yyyy"),
        });
      } else if (key === EVENT_FULL_EVENTS_FILTER && value) {
        newFilters.push({
          category: key,
          value: value as string,
          label: fullEvents as string,
        });
      } else if (key === EVENT_TOPIC_ID_FILTER && value) {
        newFilters.push({
          category: key,
          value: value as string,
          label: removeTopic as string,
        });
      } else {
        if (value !== DEFAULT_EVENTS_FILTERS.events[key as keyof EventFilters["events"]]) {
          const label = mappedEventFiltersOptions.get(value as EventsFiltersOptions);
          if (label) {
            newFilters.push({
              category: key,
              value: value as string,
              label,
            });
          }
        }
      }
    }
  }
  return newFilters;
};

export const useMembersOptions = (canSelectLittleSisters?: boolean) => {
  const intl = useIntl();

  return useMemo(() => {
    const membersOptions = [
      { value: MemberType.Members, name: intl.formatMessage({ id: "COMMON_FILTERS_MEMBERS" }) },
      { value: MemberType.BigSisters, name: intl.formatMessage({ id: "PEOPLE_BIG_SISTERS" }) },
      { value: MemberType.Follow, name: intl.formatMessage({ id: "PEOPLE_PEOPLE_I_FOLLOW" }) },
    ];
    const membersOptionsForHLT = [
      {
        value: MemberType.LittleSisters,
        name: intl.formatMessage({ id: "PEOPLE_LITTLE_SISTERS" }),
      },
      { value: MemberType.Members, name: intl.formatMessage({ id: "COMMON_FILTERS_MEMBERS" }) },
      { value: MemberType.BigSisters, name: intl.formatMessage({ id: "PEOPLE_BIG_SISTERS" }) },
      { value: MemberType.Follow, name: intl.formatMessage({ id: "PEOPLE_PEOPLE_I_FOLLOW" }) },
    ];
    return canSelectLittleSisters ? membersOptionsForHLT : membersOptions;
  }, [intl, canSelectLittleSisters]);
};

export const useEnglishLevelOptions = () => {
  const intl = useIntl();

  return useMemo(
    () => [
      {
        value: PeopleEnglishLevelOptions.Intermediate,
        name: intl.formatMessage({ id: "PEOPLE_SEARCH_INTERMEDIATE" }),
      },
      {
        value: PeopleEnglishLevelOptions.Advanced,
        name: intl.formatMessage({ id: "PEOPLE_SEARCH_ADVANCED" }),
      },
      {
        value: PeopleEnglishLevelOptions.Proficient,
        name: intl.formatMessage({ id: "PEOPLE_SEARCH_PROFICIENT" }),
      },
    ],
    [intl],
  );
};

export const usePersonalInterestsOptions = () => {
  const intl = useIntl();
  const personalInterestsOptions = useMemo(() => {
    return values(Interest).map((interest) => translateInterest(interest, intl));
  }, [intl]);

  return personalInterestsOptions;
};

export const useEnglishInterestsOptions = () => {
  const intl = useIntl();
  const englishInterestsOptions = useMemo(() => {
    return values(EnglishSkill).map((englishSkill) => translateEnglishSkill(englishSkill, intl));
  }, [intl]);
  return englishInterestsOptions;
};

export const useGetOccupationOptions = () => {
  const intl = useIntl();
  const { data } = useGetOccupationsQuery({
    fetchPolicy: "cache-and-network",
  });

  const occupationOptions = data?.getOccupations
    ? data?.getOccupations.map((occupation) => ({
        id: occupation?.id,
        name: translateEnum(intl, occupation?.name),
      }))
    : [];

  return occupationOptions;
};

export const useGetGroupedOccupationsOptions = () => {
  const intl = useIntl();
  const { data } = useGetGroupedOccupationsQuery({
    fetchPolicy: "cache-and-network",
  });

  const groupedOccupationsOptions = data?.getGroupedOccupations
    ? data?.getGroupedOccupations.map((occupation: OccupationModel) => ({
        id: occupation?.id,
        name: translateEnum(intl, occupation?.name),
        groupName: translateEnum(intl, occupation?.industry?.name),
        meta: occupation || {},
      }))
    : [];

  return groupedOccupationsOptions;
};

export const useEventTypeOptions = (attendedEventsCount: number = 0) => {
  const { me } = useAuth();
  const intl = useIntl();
  const staff = isStaff(me);

  const littleSister = me?.kind === EventPermissions.ZeroToOne;

  const isPostReleaseUser = isPostReleaseMember(me?.createdAt);
  const isAdminTeam = Boolean(me) && isAdminOrTeam(me);
  const shouldUnlockAdvancedFilters =
    isAdminTeam || not(isPostReleaseUser) || attendedEventsCount >= EVENTS_TO_UNLOCK;

  return useMemo(() => {
    const newMemberMeetup = {
      id: EventTypeFilter.NewMemberMeetup,
      name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_NEW_MEMBER_EVENTS" }),
    };

    const baseEventTypeOptions = [
      {
        id: EventTypeFilter.AllEventTypes,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_ALL_EVENT_TYPES" }),
      },
      {
        id: EventTypeFilter.Conversation,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_CONVERSATIONS" }),
      },
      {
        id: EventTypeFilter.ListenOnly,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_LISTEN_ONLY" }),
      },
      {
        id: EventTypeFilter.InPerson,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_IN_PERSON_EVENTS" }),
      },
    ];

    const advancedEventTypeOptions = shouldUnlockAdvancedFilters
      ? [
          {
            id: EventTypeFilter.StudyAndLearn,
            name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_STUDY_EVENTS" }),
          },
          {
            id: EventTypeFilter.DeepDive,
            name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_DEEP_DIVE" }),
          },
          {
            id: EventTypeFilter.Professional,
            name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_PROFESSIONAL_EVENTS" }),
          },
        ]
      : [];

    const membersEventTypeOptions = [...baseEventTypeOptions, ...advancedEventTypeOptions];

    if (staff) {
      return [...membersEventTypeOptions, newMemberMeetup];
    }

    // If user is not Hey Lady, only show NMM if they haven't gotten the badge
    return littleSister ? [...membersEventTypeOptions, newMemberMeetup] : membersEventTypeOptions;
  }, [intl, shouldUnlockAdvancedFilters, staff, littleSister]);
};

export const useEventEnglishLevelOptions = () => {
  const intl = useIntl();

  return useMemo(
    () => [
      {
        id: EventEnglishLevelFilter.All,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_EVENT_LEVEL" }),
      },
      {
        id: EventEnglishLevelFilter.AnyLevel,
        name: intl.formatMessage({ id: "COMMON_ANY_LEVEL" }),
      },
      {
        id: EventEnglishLevelFilter.Intermediate,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_INTERMEDIATE" }),
      },
      {
        id: EventEnglishLevelFilter.Advanced,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_ADVANCED" }),
      },
    ],
    [intl],
  );
};

export const useEventTimeOptions = () => {
  const intl = useIntl();

  return useMemo(
    () => [
      {
        id: EventTimeFilter.Morning,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_TIME_MORNING" }),
      },
      {
        id: EventTimeFilter.Afternoon,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_TIME_AFTERNOON" }),
      },
      {
        id: EventTimeFilter.Evening,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_TIME_EVENING" }),
      },
      {
        id: EventTimeFilter.Night,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_TIME_NIGHT" }),
      },
      {
        id: EventTimeFilter.Custom,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_CUSTOM_TIME" }),
      },
    ],
    [intl],
  );
};

export const useEventTimeOptionsMap = () => {
  const options = useEventTimeOptions();
  return new Map(options.map((option) => [option.id, option.name]));
};

export const useEventHostedByOptions = (
  attendedEventsCount: number = 0,
  userCreatedAt?: string | number | Date,
) => {
  const { me } = useAuth();
  const intl = useIntl();
  const isPostReleaseUser = isPostReleaseMember(userCreatedAt);
  const isAdminTeam = Boolean(me) && isAdminOrTeam(me);
  const shouldUnlockAdvancedFilters =
    isAdminTeam || not(isPostReleaseUser) || attendedEventsCount >= EVENTS_TO_UNLOCK;

  return useMemo(() => {
    const baseHostOptions = [
      {
        id: EventHostedByFilter.Anyone,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_HOSTED_BY_ANYONE" }),
      },
      {
        id: EventHostedByFilter.HlTeam,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_TEAM" }, { HL: <LogoBranding /> }),
      },
      {
        id: EventHostedByFilter.PeopleIFollow,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_PEOPLE_I_FOLLOW" }),
      },
      {
        id: EventHostedByFilter.BigSisters,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_BIG_SISTERS" }),
      },
    ];

    const advancedHostOptions = shouldUnlockAdvancedFilters
      ? [
          {
            id: EventHostedByFilter.HlMembers,
            name: intl.formatMessage(
              { id: "EVENTS_PAGE_FILTERS_HL_MEMBERS" },
              { HL: <LogoBranding /> },
            ),
          },
          {
            id: EventHostedByFilter.HlCoach,
            name: intl.formatMessage(
              { id: "EVENTS_PAGE_FILTERS_HL_COACHES" },
              { HL: <LogoBranding /> },
            ),
          },
        ]
      : [];

    return [...baseHostOptions, ...advancedHostOptions];
  }, [intl, shouldUnlockAdvancedFilters]);
};

export const useEventTopicFilterOptions = () => {
  const intl = useIntl();

  return useMemo(
    () => [
      {
        id: EventTopicFilter.All,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_ALL_TOPICS" }),
      },
      {
        id: EventTopicFilter.HlTopics,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_HL_TOPICS" }, { HL: <LogoBranding /> }),
      },
      {
        id: EventTopicFilter.Other,
        name: intl.formatMessage({ id: "EVENTS_PAGE_FILTERS_OTHER_TOPICS" }),
      },
    ],
    [intl],
  );
};

export const useEventDurationOptions = (
  attendedEventsCount: number = 0,
  userCreatedAt?: string | number | Date,
) => {
  const { me } = useAuth();
  const intl = useIntl();

  const isPostReleaseUser = isPostReleaseMember(userCreatedAt);
  const isAdminTeam = Boolean(me) && isAdminOrTeam(me);
  const shouldUnlockAdvancedFilters =
    isAdminTeam || not(isPostReleaseUser) || attendedEventsCount >= EVENTS_TO_UNLOCK;

  const filteredDurations = shouldUnlockAdvancedFilters
    ? DURATION
    : DURATION.filter((duration) => duration.value < 45);

  return filteredDurations.map((duration) => ({
    id: duration.value,
    label: translateEventDurationFilter(duration.value, intl).title,
  }));
};

export const useEventDurationOptionsMap = (
  attendedEventsCount: number = 0,
  userCreatedAt?: string | number | Date,
) => {
  const options = useEventDurationOptions(attendedEventsCount, userCreatedAt);
  return new Map(options.map((option) => [option.id, option.label]));
};

type EventFiltersToGet =
  | typeof EventTypeFilter
  | typeof EventHostedByFilter
  | typeof EventEnglishLevelFilter
  | typeof EventTopicFilter;
const getFilter = <T extends EventFiltersToGet>(
  filter: T,
  value: string | null,
  defaultValue: T[keyof T],
): T[keyof T] => {
  if (value && Object.values(filter).includes(value)) {
    return value as T[keyof T];
  }
  return defaultValue;
};

export const useGetEventFiltersFromUrl = () => {
  const { search } = useLocation();

  const query = new URLSearchParams(search);
  const type = getFilter(
    EventTypeFilter,
    query.get(UrlFilterParams.Type),
    EventTypeFilter.AllEventTypes,
  );
  const host = getFilter(
    EventHostedByFilter,
    query.get(UrlFilterParams.Host),
    EventHostedByFilter.Anyone,
  );
  const englishLevel = getFilter(
    EventEnglishLevelFilter,
    query.get(UrlFilterParams.EnglishLevel),
    EventEnglishLevelFilter.All,
  );
  const showFullEvents = query.get(UrlFilterParams.Full) === "true";
  const speakingEvents = query.get(UrlFilterParams.SpeakingEvents) === "true";
  const groupStudy = query.get(UrlFilterParams.GroupStudy) === "true";

  const eventDateString = query.get(UrlFilterParams.EventDate);
  const eventDate = eventDateString ?? undefined;

  const eventTimeParam = query.get(UrlFilterParams.EventTime);
  const eventTime = getEventTimeFiltersFromParam(
    eventTimeParam,
    DEFAULT_EVENTS_FILTERS.events.eventTime,
  );

  const customTimeFrom = query.get(UrlFilterParams.CustomTimeFrom) ?? undefined;
  const customTimeUntil = query.get(UrlFilterParams.CustomTimeUntil) ?? undefined;

  const topicId = query.get(UrlFilterParams.TopicId) ?? undefined;
  const topicFilter = getFilter(
    EventTopicFilter,
    query.get(UrlFilterParams.TopicFilter),
    EventTopicFilter.All,
  );
  const interests =
    (query?.get(UrlFilterParams.Interests)?.split(",").filter(isNotEmpty) as Interest[]) || [];
  const englishSkills =
    (query?.get(UrlFilterParams.EnglishSkills)?.split(",").filter(isNotEmpty) as EnglishSkill[]) ||
    [];
  const occupations = query?.get(UrlFilterParams.Occupations)?.split(",").filter(isNotEmpty) || [];
  const duration =
    query?.get(UrlFilterParams.Duration)?.split(",").filter(isNotEmpty).map(Number) || [];

  return {
    type,
    host,
    englishLevel,
    showFullEvents,
    speakingEvents,
    groupStudy,
    eventDate,
    eventTime,
    customTimeFrom,
    customTimeUntil,
    topicId,
    topicFilter,
    interests,
    englishSkills,
    occupations,
    duration,
  };
};
